import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Text, Button, IconButton, HStack, VStack, Flex,
  useColorModeValue, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalFooter, ModalBody, ModalCloseButton, FormControl, FormLabel,
  Input, Textarea, useDisclosure, useToast, InputGroup,
  InputLeftElement, Card, CardBody, Stat, StatLabel, StatNumber,
  SimpleGrid, Menu, MenuButton, MenuList, MenuItem, Spinner, Center,
  Alert, AlertIcon, AlertTitle, AlertDescription, Tooltip, Tag, TagLabel, Select
} from '@chakra-ui/react';
import {
  MdSearch, MdRefresh, MdAdd, MdMoreVert, MdEdit, MdDelete,
  MdVisibility, MdCode, MdDynamicForm,
} from 'react-icons/md';
import apiService from '../services/apiService';
import { useNavigate } from 'react-router-dom';
import SqlEditor from '../components/Common/SQLEditor';

// ─────────────────────────────────────────────────────────────────────────────
// API field mapping (from actual response):
//   id, name, description, reportQuery, updatedAt, updatedBy, isDelete
// ─────────────────────────────────────────────────────────────────────────────

const DynamicReports = ({ userProfile, appConfig, authServiceReady }) => {

  // ── Color mode tokens ────────────────────────────────────────────────────
  const bgColor           = useColorModeValue('gray.50',   'gray.900');
  const cardBg            = useColorModeValue('white',     'gray.800');
  const borderColor       = useColorModeValue('gray.200',  'gray.700');
  const textColor         = useColorModeValue('gray.600',  'gray.300');
  const headingColor      = useColorModeValue('gray.800',  'white');
  const mutedTextColor    = useColorModeValue('gray.500',  'gray.400');
  const inputBg           = useColorModeValue('white',     'gray.700');
  const hoverBg           = useColorModeValue('gray.50',   'gray.700');
  const tableHeaderBg     = useColorModeValue('gray.50',   'gray.900');
  const statsCardBg       = useColorModeValue('white',     'gray.800');
  const btnHoverBorder    = useColorModeValue('gray.300',  'gray.600');
  const purpleHoverBg     = useColorModeValue('purple.50', 'purple.900');
  const purpleBorder      = useColorModeValue('purple.200','purple.600');
  const purpleText        = useColorModeValue('purple.600','purple.300');
  const purpleHoverBorder = useColorModeValue('purple.300','purple.500');
  const blueHover         = useColorModeValue('blue.600',  'blue.500');
  const modalOverlay      = useColorModeValue('blackAlpha.300', 'blackAlpha.700');
  const menuShadow        = useColorModeValue('lg',        'dark-lg');
  const redHoverBg        = useColorModeValue('red.50',    'red.900');
  const searchIconColor   = useColorModeValue('gray.400',  'gray.500');
  const tagSqlBg          = useColorModeValue('blue.50',   'blue.900');
  const tagSqlColor       = useColorModeValue('blue.600',  'blue.200');
  const descBg            = useColorModeValue('gray.50',   'gray.700');
  const metaBg            = useColorModeValue('gray.50',   'gray.700');

  const sqlEditorBg    = useColorModeValue('gray.900',  'gray.950');
  const sqlEditorBorder= useColorModeValue('gray.600',  'gray.700');
  const sqlEditorHdrBg = useColorModeValue('gray.800',  'gray.900');
  const sqlEditorHdrBd = useColorModeValue('gray.600',  'gray.700');
  const sqlTagBg       = useColorModeValue('blue.600',  'blue.500');
  const sqlCharColor   = useColorModeValue('gray.400',  'gray.500');
  const sqlPlaceholder = useColorModeValue('gray.600',  'gray.600');
  const viewSqlBodyBg  = useColorModeValue('gray.900',  'gray.950');
  const copyBtnBg      = useColorModeValue('gray.700',  'gray.600');
  const copyBtnHoverBg = useColorModeValue('gray.600',  'gray.500');

  const sqlTokens = {
    sqlEditorBg, sqlEditorBorder, sqlEditorHdrBg, sqlEditorHdrBd,
    sqlTagBg, sqlCharColor, sqlPlaceholder, viewSqlBodyBg, copyBtnBg, copyBtnHoverBg,
  };

  const { isOpen, onOpen, onClose }                                      = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const toast    = useToast();
  const navigate = useNavigate();

  const [reports,     setReports]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [pageSize,    setPageSize]    = useState('20 per page');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditMode,  setIsEditMode]  = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  // Form fields match API: name, description, reportQuery
  const emptyForm = { name: '', description: '', reportQuery: '' };
  const [formData,   setFormData]   = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete,    setItemToDelete]    = useState(null);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchReports = async () => {
    try {
      setLoading(true); setError(null);
      const res = await apiService.fetchDynamicReports();
      if (res.success && res.data) setReports(Array.isArray(res.data) ? res.data : []);
      else throw new Error(res.error || res.message || 'Failed to fetch dynamic reports');
    } catch (err) {
      console.error(err);
      setError(err.message);
      setReports([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReports(); }, []);

  // ─── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:     reports.length,
    withQuery: reports.filter(r => r.reportQuery?.trim()).length,
    recent:    reports.filter(r => {
      if (!r.updatedAt) return false;
      return (Date.now() - new Date(r.updatedAt)) < 7 * 24 * 60 * 60 * 1000;
    }).length,
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const handleSearch  = val => { setSearchTerm(val); setCurrentPage(1); };
  const resetFilters  = ()  => { setSearchTerm('');  setCurrentPage(1); };
  const handleRefresh = ()  => {
    fetchReports();
    toast({ title: 'Refreshed', status: 'info', duration: 2000, isClosable: true });
  };

  const handleCreateNew  = () => { setIsEditMode(false); setEditingItem(null); setFormData(emptyForm); setFormErrors({}); onOpen(); };
  const handleModalClose = () => { setIsEditMode(false); setEditingItem(null); setFormData(emptyForm); setFormErrors({}); onClose(); };

  // ─── SELECT-only validation ───────────────────────────────────────────────
  const validateSelectOnly = (sql) => {
    const cleaned = sql.trim().replace(/\/\*[\s\S]*?\*\//g, '').replace(/--[^\n]*/g, '').trim();
    const firstWord = cleaned.split(/\s+/)[0].toUpperCase();
    if (!firstWord) return 'SQL query is required.';
    if (firstWord !== 'SELECT') return `Only SELECT queries are allowed. "${firstWord}" statements are not permitted.`;
    const forbidden = /\b(INSERT|UPDATE|DELETE|DROP|TRUNCATE|ALTER|CREATE|EXEC|EXECUTE|MERGE|REPLACE|CALL)\b/i;
    if (forbidden.test(cleaned)) return 'Query must not contain data-modifying statements (INSERT, UPDATE, DELETE, DROP, etc.).';
    return null;
  };

  const validate = () => {
    const e = {};
    if (!formData.name?.trim()) e.name = 'Report name is required.';
    const sqlErr = validateSelectOnly(formData.reportQuery || '');
    if (sqlErr) e.reportQuery = sqlErr;
    return e;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setFormErrors(e); return; }
    const payload = {
      name:        formData.name.trim(),
      description: formData.description.trim(),
      reportQuery: formData.reportQuery.trim(),
      updatedBy:   userProfile.email,
    };
    console.log(payload)
    try {
      if (isEditMode) {
        payload.id = editingItem.id;
        const res = await apiService.updateDynamicReport(payload);
        if (res.success) {
          handleRefresh(); handleModalClose();
          toast({ title: 'Report Updated', description: `"${payload.name}" updated.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to update');
      } else {
        const res = await apiService.createDynamicReport(payload);
        if (res.success) {
          handleRefresh(); handleModalClose();
          toast({ title: 'Report Created', description: `"${payload.name}" created.`, status: 'success', duration: 3000, isClosable: true });
        } else throw new Error(res.error || res.message || 'Failed to create');
      }
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: err.message || 'Operation failed.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleEdit = item => {
    setIsEditMode(true); setEditingItem(item);
    setFormData({
      name:        item.name        || '',
      description: item.description || '',
      reportQuery: item.reportQuery || '',
    });
    setFormErrors({}); onOpen();
  };

  const handleView = item => { setViewingItem(item); onViewOpen(); };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDelete  = item => { setItemToDelete(item); setDeleteModalOpen(true); };
  const cancelDelete  = ()   => { setDeleteModalOpen(false); setItemToDelete(null); };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await apiService.deleteDynamicReport(itemToDelete.id);
      if (res.success) {
        handleRefresh();
        toast({ title: 'Deleted', description: `"${itemToDelete.name}" deleted.`, status: 'success', duration: 3000, isClosable: true });
        cancelDelete();
      } else throw new Error('Failed to delete');
    } catch {
      toast({ title: 'Error', description: 'Failed to delete report.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // Navigate to mappings page for this report
  const handleReportNameClick = useCallback((id) => {
    try {
      navigate(`/dynamic-report-mappings?reportId=${id}`);
    } catch (err) {
      console.error('Failed to navigate to report mappings:', err);
    }
  }, [navigate]);

  // ─── Filter & Paginate ────────────────────────────────────────────────────
  const filtered = reports.filter(r => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
           (r.description || '').toLowerCase().includes(q);
  });

  const itemsPerPage = parseInt(pageSize.split(' ')[0]);
  const totalPages   = Math.ceil(filtered.length / itemsPerPage);
  const startIndex   = (currentPage - 1) * itemsPerPage;
  const paginated    = filtered.slice(startIndex, startIndex + itemsPerPage);

  // updatedAt comes as ISO string e.g. "2026-03-11T07:54:19.615880" (no Z) — treat as UTC
  const formatDateTime = d => {
    if (!d) return 'N/A';
    const date = new Date(d.includes('Z') ? d : d + 'Z');
    return isNaN(date) ? 'Invalid Date' : date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  const getTimeAgo = d => {
    if (!d) return 'Never';
    const past = new Date(d.includes('Z') ? d : d + 'Z');
    if (isNaN(past)) return 'Invalid Date';
    const mins = Math.floor((Date.now() - past) / 60000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7)   return `${days}d ago`;
    const wks = Math.floor(days / 7);
    if (wks < 4)    return `${wks}w ago`;
    const mos = Math.floor(days / 30);
    if (mos < 12)   return `${mos}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  // ─── Loading / Error screens ──────────────────────────────────────────────
  if (loading) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Center py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textColor} fontSize="lg">Loading dynamic reports...</Text>
        </VStack>
      </Center>
    </Box>
  );

  if (error) return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Alert status="error" bg={cardBg} borderColor="red.300" borderWidth="1px" borderRadius="md">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle color="red.600">Error!</AlertTitle>
          <AlertDescription color={textColor}>{error}</AlertDescription>
        </Box>
        <Button size="sm" colorScheme="red" variant="outline" onClick={handleRefresh}>Retry</Button>
      </Alert>
    </Box>
  );

  return (
    <Box bg={bgColor} minH="100vh" p={8}>

      {/* ── Header ── */}
      <Flex justify="space-between" align="flex-start" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={headingColor} mb={1}>Dynamic Reports</Text>
          <Text color={textColor} fontSize="sm">Manage SQL-based dynamic reports for clients</Text>
        </Box>
        <HStack spacing={3}>
          <Button leftIcon={<MdRefresh />} variant="outline" size="sm" borderColor={borderColor} color={textColor} onClick={handleRefresh} _hover={{ bg: hoverBg, borderColor: btnHoverBorder }}>Refresh</Button>
          <Button leftIcon={<MdAdd />} colorScheme="blue" size="sm" onClick={handleCreateNew} _hover={{ bg: blueHover }}>Add Report</Button>
        </HStack>
      </Flex>

      {/* ── Stats ── */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">Total Reports</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>{stats.total}</StatNumber></Stat></CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">With SQL Query</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="blue.500">{stats.withQuery}</StatNumber></Stat></CardBody>
        </Card>
        <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <CardBody><Stat><StatLabel color={textColor} fontSize="sm">Updated This Week</StatLabel><StatNumber fontSize="2xl" fontWeight="bold" color="green.500">{stats.recent}</StatNumber></Stat></CardBody>
        </Card>
      </SimpleGrid>

      {/* ── Filters ── */}
      <Card bg={cardBg} boxShadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <Flex gap={4} align="center">
            <Box flex="1">
              <InputGroup>
                <InputLeftElement pointerEvents="none"><MdSearch color={searchIconColor} /></InputLeftElement>
                <Input
                  placeholder="Search by name or description"
                  value={searchTerm}
                  onChange={e => handleSearch(e.target.value)}
                  bg={inputBg} borderColor={borderColor} color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                />
              </InputGroup>
            </Box>
            <FormControl maxW="180px">
              <Select
                size="md" value={pageSize}
                onChange={e => { setPageSize(e.target.value); setCurrentPage(1); }}
                bg={inputBg} borderColor={borderColor} color={headingColor}
              >
                <option value="20 per page">20 per page</option>
                <option value="50 per page">50 per page</option>
                <option value="100 per page">100 per page</option>
              </Select>
            </FormControl>
            <Button
              variant="outline" colorScheme="purple" size="md" onClick={resetFilters}
              borderColor={purpleBorder} color={purpleText}
              _hover={{ bg: purpleHoverBg, borderColor: purpleHoverBorder }}
            >Reset Filters</Button>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Table ── */}
      <Card bg={cardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderBottomColor={borderColor}>
            <Text fontWeight="semibold" fontSize="md" color={headingColor}>Reports ({filtered.length})</Text>
            <HStack spacing={4} fontSize="sm" color={textColor}>
              <Text>PAGE {currentPage} OF {totalPages || 1}</Text>
              <Text>SHOWING {Math.min(itemsPerPage, filtered.length - startIndex)} OF {filtered.length}</Text>
            </HStack>
          </Flex>

          {filtered.length === 0 ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Box color={mutedTextColor}><MdDynamicForm size={40} /></Box>
                <Text color={mutedTextColor} fontSize="lg">No dynamic reports found</Text>
                <Text color={mutedTextColor} fontSize="sm">{searchTerm ? 'Try adjusting your search' : 'Add your first dynamic report to get started'}</Text>
              </VStack>
            </Center>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg} borderBottom="2px" borderColor={borderColor}>
                    <Tr>
                      {['Report Name', 'Description', 'Updated By', 'Last Updated', 'Actions'].map(h => (
                        <Th key={h} color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>{h}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginated.map((item, idx) => (
                      <Tr key={`${item.id}-${idx}`} _hover={{ bg: hoverBg }}>

                        {/* Report Name */}
                        <Td py={4} borderColor={borderColor} maxW="220px" onClick={() => handleReportNameClick(item.id)}>
                          <div style={{ cursor: 'pointer' }}>
                            <VStack align="start" spacing={1}>
                              <Text fontSize="sm" fontWeight="semibold" color={headingColor} isTruncated maxW="200px">{item.name}</Text>
                              <HStack spacing={2}>
                                <Tag size="sm" bg={tagSqlBg} borderRadius="sm">
                                  <TagLabel fontSize="10px" color={tagSqlColor} fontWeight="bold">
                                    <HStack spacing={1}><MdCode size={10} /><Text>SQL</Text></HStack>
                                  </TagLabel>
                                </Tag>
                                <Text fontSize="xs" color={mutedTextColor}>ID: {item.id}</Text>
                              </HStack>
                            </VStack>
                          </div>
                        </Td>

                        {/* Description */}
                        <Td py={4} borderColor={borderColor} maxW="300px">
                          <Tooltip label={item.description} placement="top" isDisabled={!item.description || item.description.length <= 90}>
                            <Text fontSize="sm" color={textColor} noOfLines={2}>
                              {item.description || <Text as="span" color={mutedTextColor} fontStyle="italic">No description</Text>}
                            </Text>
                          </Tooltip>
                        </Td>

                        {/* Updated By */}
                        <Td py={4} borderColor={borderColor} maxW="180px">
                          <Text fontSize="sm" color={textColor} isTruncated maxW="160px">
                            {item.updatedBy || <Text as="span" color={mutedTextColor} fontStyle="italic">—</Text>}
                          </Text>
                        </Td>

                        {/* Last Updated */}
                        <Td py={4} borderColor={borderColor} minW="160px">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" color={headingColor} fontWeight="medium">{getTimeAgo(item.updatedAt)}</Text>
                            <Text fontSize="xs" color={mutedTextColor}>{formatDateTime(item.updatedAt)}</Text>
                          </VStack>
                        </Td>

                        {/* Actions */}
                        <Td py={4} borderColor={borderColor}>
                          <Menu>
                            <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" color={textColor} _hover={{ bg: hoverBg, color: headingColor }} />
                            <MenuList bg={cardBg} borderColor={borderColor} boxShadow={menuShadow}>
                              <MenuItem icon={<MdVisibility />} onClick={() => handleView(item)}   bg={cardBg} color={headingColor} _hover={{ bg: hoverBg }}>View</MenuItem>
                              <MenuItem icon={<MdEdit />}       onClick={() => handleEdit(item)}   bg={cardBg} color={headingColor} _hover={{ bg: hoverBg }}>Edit</MenuItem>
                              <MenuItem icon={<MdDelete />}     onClick={() => handleDelete(item)} bg={cardBg} color="red.500"      _hover={{ bg: redHoverBg }}>Delete</MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>

                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                  <Text fontSize="sm" color={textColor}>
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filtered.length)} of {filtered.length} entries
                  </Text>
                  <HStack spacing={2}>
                    {['First', 'Previous'].map(lbl => (
                      <Button key={lbl} size="sm"
                        onClick={() => setCurrentPage(lbl === 'First' ? 1 : currentPage - 1)}
                        isDisabled={currentPage === 1}
                        variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}
                      >{lbl}</Button>
                    ))}
                    {(() => {
                      const pages = [], maxV = 5;
                      let s = Math.max(1, currentPage - 2), e = Math.min(totalPages, s + maxV - 1);
                      if (e - s < maxV - 1) s = Math.max(1, e - maxV + 1);
                      for (let i = s; i <= e; i++) pages.push(
                        <Button key={i} size="sm"
                          variant={i === currentPage ? 'solid' : 'outline'}
                          colorScheme={i === currentPage ? 'blue' : 'gray'}
                          onClick={() => setCurrentPage(i)}
                          borderColor={i === currentPage ? 'blue.500' : borderColor}
                          color={i === currentPage ? 'white' : textColor}
                          bg={i === currentPage ? 'blue.500' : 'transparent'}
                          _hover={{ bg: i === currentPage ? 'blue.600' : hoverBg }}
                        >{i}</Button>
                      );
                      return pages;
                    })()}
                    {['Next', 'Last'].map(lbl => (
                      <Button key={lbl} size="sm"
                        onClick={() => setCurrentPage(lbl === 'Last' ? totalPages : currentPage + 1)}
                        isDisabled={currentPage === totalPages}
                        variant="outline" borderColor={borderColor} color={textColor} _hover={{ bg: hoverBg }}
                      >{lbl}</Button>
                    ))}
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px" maxH="90vh">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor} pb={4}>
            <HStack spacing={3}>
              <Box color="blue.500"><MdDynamicForm size={22} /></Box>
              <VStack align="start" spacing={0}>
                <Text>{isEditMode ? 'Edit Dynamic Report' : 'Add New Dynamic Report'}</Text>
                {isEditMode && <Text fontSize="xs" color={mutedTextColor} fontWeight="normal">ID: {editingItem?.id}</Text>}
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />

          <ModalBody py={6} overflowY="auto">
            <VStack spacing={5} align="stretch">

              {/* Name */}
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Report Name</FormLabel>
                <Input
                  placeholder="e.g. Monthly Revenue Summary"
                  value={formData.name}
                  onChange={e => { setFormData({ ...formData, name: e.target.value }); setFormErrors({ ...formErrors, name: '' }); }}
                  bg={inputBg} borderColor={borderColor} color={headingColor}
                  _placeholder={{ color: mutedTextColor }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                />
                {formErrors.name && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.name}</Text>}
              </FormControl>

              {/* Description */}
              <FormControl>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                  Description <Text as="span" color={mutedTextColor} fontWeight="normal">(optional)</Text>
                </FormLabel>
                <Textarea
                  placeholder="Brief description of what this report generates..."
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  bg={inputBg} borderColor={borderColor} color={headingColor} rows={3} resize="vertical"
                  _placeholder={{ color: mutedTextColor }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                />
              </FormControl>

              {/* SQL Query */}
              <FormControl isRequired isInvalid={!!formErrors.reportQuery}>
                <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                  <HStack spacing={2}><MdCode size={16} /><Text>Report SQL Query</Text></HStack>
                </FormLabel>
                <SqlEditor
                  value={formData.reportQuery}
                  onChange={val => {
                    setFormData({ ...formData, reportQuery: val });
                    setFormErrors({ ...formErrors, reportQuery: '' });
                  }}
                  height="320px"
                  tokens={sqlTokens}
                  formError={formErrors.reportQuery}
                  toast={toast}
                />
                {formErrors.reportQuery && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.reportQuery}</Text>}
                <Text fontSize="xs" color={mutedTextColor} mt={1.5}>
                  Only <Text as="span" color="blue.400" fontWeight="semibold">SELECT</Text> queries are allowed.
                  INSERT, UPDATE, DELETE, DROP and other data-modifying statements will be rejected.
                </Text>
              </FormControl>

            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="blue" mr={3} onClick={handleSave} _hover={{ bg: blueHover }}>
              {isEditMode ? 'Update Report' : 'Add Report'}
            </Button>
            <Button variant="ghost" onClick={handleModalClose} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── View Modal ── */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px" maxH="90vh">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor} pb={4}>
            <HStack spacing={3}>
              <Box color="green.500"><MdVisibility size={22} /></Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="lg">{viewingItem?.name}</Text>
                <Text fontSize="xs" color={mutedTextColor} fontWeight="normal">ID: {viewingItem?.id}</Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />

          <ModalBody py={6} overflowY="auto">
            <VStack spacing={5} align="stretch">

              {/* Description */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={2}>Description</Text>
                <Box px={4} py={3} bg={descBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Text
                    fontSize="sm"
                    color={viewingItem?.description ? headingColor : mutedTextColor}
                    fontStyle={viewingItem?.description ? 'normal' : 'italic'}
                  >
                    {viewingItem?.description || 'No description provided.'}
                  </Text>
                </Box>
              </Box>

              {/* Meta */}
              <HStack spacing={8} bg={metaBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} px={4} py={3} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Last Updated</Text>
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{formatDateTime(viewingItem?.updatedAt)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Time Ago</Text>
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{getTimeAgo(viewingItem?.updatedAt)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Updated By</Text>
                  <Text fontSize="sm" color={headingColor} fontWeight="medium">{viewingItem?.updatedBy || '—'}</Text>
                </Box>
              </HStack>

              {/* SQL Query */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={2}>SQL Query</Text>
                <SqlEditor
                  value={viewingItem?.reportQuery || ''}
                  isReadOnly
                  height="360px"
                  tokens={sqlTokens}
                  toast={toast}
                />
              </Box>

            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="blue" mr={3} leftIcon={<MdEdit />} onClick={() => { onViewClose(); handleEdit(viewingItem); }} _hover={{ bg: blueHover }}>Edit</Button>
            <Button variant="ghost" onClick={onViewClose} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal isOpen={deleteModalOpen} onClose={cancelDelete} size="md" isCentered>
        <ModalOverlay bg={modalOverlay} />
        <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
            <HStack spacing={3}><Box color="red.500"><MdDelete size={24} /></Box><Text>Confirm Deletion</Text></HStack>
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" variant="left-accent" borderRadius="md">
                <AlertIcon />
                <Box><AlertTitle fontSize="sm">Warning</AlertTitle><AlertDescription fontSize="sm">This action cannot be undone.</AlertDescription></Box>
              </Alert>
              <Text color={textColor} fontSize="sm">Are you sure you want to delete the following report?</Text>
              <Card bg={hoverBg} borderWidth="1px" borderColor={borderColor}>
                <CardBody p={3}>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={mutedTextColor}>Name:</Text>
                      <Text fontSize="sm" color={headingColor} fontWeight="medium">{itemToDelete?.name}</Text>
                    </HStack>
                    <HStack justify="space-between" align="start">
                      <Text fontSize="xs" color={mutedTextColor} flexShrink={0}>Description:</Text>
                      <Text fontSize="sm" color={textColor} textAlign="right" maxW="260px" noOfLines={2}>{itemToDelete?.description || '—'}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color={mutedTextColor}>ID:</Text>
                      <Text fontSize="sm" color={textColor}>{itemToDelete?.id}</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderTopColor={borderColor}>
            <Button colorScheme="red" mr={3} onClick={confirmDelete} leftIcon={<MdDelete />}>Delete Report</Button>
            <Button variant="ghost" onClick={cancelDelete} color={textColor} _hover={{ bg: hoverBg, color: headingColor }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default DynamicReports;